import UsageSparkline from '@components/Reporting/UsageSparkline';
import Header from '@components/Shared/Layout/Header';
import {
  Alert, Box, Card, CardContent, Chip, MenuItem, Pagination, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import {
  useGetActivationLogsQuery, useGetActivationLogSummaryQuery, useGetSeatUsageSeriesQuery,
  type ActivationLogQuery, type ActivationOutcome
} from '@store/api/activationLogEndpoints';
import { useGetLicensesQuery } from '@store/api/licenseEndpoints';
import { useMemo, useState } from 'react';

const RANGES = [
  { label: 'Last 24 hours', hours: 24 },
  { label: 'Last 7 days', hours: 24 * 7 },
  { label: 'Last 30 days', hours: 24 * 30 },
  { label: 'Last 12 months', hours: 24 * 365 },
];

const OUTCOMES: { value: ActivationOutcome | '', label: string }[] = [
  { value: '', label: 'All outcomes' },
  { value: 'seatGranted', label: 'Seat granted' },
  { value: 'seatReclaimed', label: 'Seat reclaimed' },
  { value: 'seatReleased', label: 'Seat released' },
  { value: 'seatRevoked', label: 'Seat revoked by admin' },
  { value: 'seatDenied', label: 'Seat denied' },
  { value: 'authFailed', label: 'Authentication failed' },
  { value: 'rejected', label: 'Rejected' },
];

/**
 * Colour carries the same meaning everywhere: red is a refusal, green is a grant, grey is a seat
 * ending on its own. A revocation is none of those. It is somebody stepping in, and on a screen
 * read to explain a dip in usage that is the row worth finding first.
 */
const outcomeColour = (outcome: ActivationOutcome) => {
  if (outcome === 'seatGranted' || outcome === 'seatReclaimed') return 'success' as const;
  if (outcome === 'seatReleased') return 'default' as const;
  if (outcome === 'seatRevoked') return 'warning' as const;

  return 'error' as const;
};

const formatDuration = (seconds?: number) => {
  if (seconds == null) return '-';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;

  const hours = Math.floor(seconds / 3600);

  return `${hours}h ${Math.round((seconds % 3600) / 60)}m`;
};

const ActivationLogsPage = () => {
  const [hours, setHours] = useState(24 * 7);
  const [licenseId, setLicenseId] = useState('');
  const [outcome, setOutcome] = useState<ActivationOutcome | ''>('');
  const [page, setPage] = useState(1);

  const { data: licenses = [] } = useGetLicensesQuery();

  // Recomputed only when the range changes, so paging or filtering does not silently slide
  // the window forward and renumber the rows underneath the operator.
  const range = useMemo(() => ({
    from: new Date(Date.now() - hours * 60 * 60 * 1000).toISOString(),
    to: new Date().toISOString()
  }), [hours]);

  const query: ActivationLogQuery = {
    ...range,
    licenseId: licenseId || undefined,
    outcome: outcome || undefined
  };

  const { data: summary } = useGetActivationLogSummaryQuery(query);
  const { data: series = [] } = useGetSeatUsageSeriesQuery(query);
  const { data: logs } = useGetActivationLogsQuery({ ...query, page, limit: 50 });

  const entitled = summary ? summary.entitled.supar + summary.entitled.composer : 0;
  const peak = summary?.peakConcurrent;
  const current = summary?.currentInUse;

  const figure = (label: string, value: string, hint?: string) => (
    <Box sx={{ minWidth: 130 }}>
      <Typography variant='caption' color='text.secondary' display='block'>{label}</Typography>
      <Typography variant='h6' sx={{ lineHeight: 1.2 }}>{value}</Typography>
      {hint && <Typography variant='caption' color='text.secondary'>{hint}</Typography>}
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Header
        title='Activity'
        description='Seat usage over time and the events behind it.'
      />

      <Alert severity='info' sx={{ mb: 2 }}>
        Events are recorded without device names or IP addresses. A device appears as a hash
        that stays the same for one calendar month and changes the next, so an incident can be
        followed without building a history of anybody.
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction='row' spacing={2} sx={{ mb: 3, flexWrap: 'wrap', rowGap: 2 }}>
            <TextField
              select size='small' label='Range' value={hours} sx={{ minWidth: 170 }}
              onChange={event => { setHours(Number(event.target.value)); setPage(1); }}
            >
              {RANGES.map(item => <MenuItem key={item.hours} value={item.hours}>{item.label}</MenuItem>)}
            </TextField>

            <TextField
              select size='small' label='License' value={licenseId} sx={{ minWidth: 200 }}
              onChange={event => { setLicenseId(event.target.value); setPage(1); }}
            >
              <MenuItem value=''>All licenses</MenuItem>
              {licenses.map(license => (
                <MenuItem key={license.licenseId} value={license.licenseId}>
                  {license.label || license.id || license.licenseId.slice(0, 8)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select size='small' label='Outcome' value={outcome} sx={{ minWidth: 190 }}
              onChange={event => { setOutcome(event.target.value as ActivationOutcome | ''); setPage(1); }}
            >
              {OUTCOMES.map(item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
            </TextField>
          </Stack>

          <Stack direction='row' spacing={4} sx={{ mb: 3, flexWrap: 'wrap', rowGap: 2 }}>
            {figure('Peak concurrent', peak ? String(peak.supar + peak.composer) : '-',
              peak ? `SuPAR ${peak.supar} · Composer ${peak.composer}` : 'no samples in this window')}
            {figure('In use now', current ? String(current.supar + current.composer) : '-',
              current ? `SuPAR ${current.supar} · Composer ${current.composer}` : undefined)}
            {figure('Entitled', String(entitled),
              summary ? `SuPAR ${summary.entitled.supar} · Composer ${summary.entitled.composer}` : undefined)}
            {figure('Average hold', formatDuration(summary?.averageHeldSeconds ?? undefined),
              summary ? `${summary.closedSessions} completed session(s)` : undefined)}
          </Stack>

          <UsageSparkline points={series} limit={entitled} />

          {summary && Object.keys(summary.refusalsByReason).length > 0 && (
            <Stack direction='row' spacing={1} sx={{ mt: 3, flexWrap: 'wrap', rowGap: 1 }}>
              {Object.entries(summary.refusalsByReason).map(([reason, count]) => (
                <Chip key={reason} size='small' color='error' variant='outlined' label={`${reason} ${count}`} />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant='h6' gutterBottom>Events</Typography>

          {!logs?.items.length ? (
            <Typography variant='body2' color='text.secondary'>Nothing recorded in this window.</Typography>
          ) : (
            <>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Outcome</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Application</TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell align='right'>Held</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.items.map((entry, index) => (
                    <TableRow key={`${entry.createdAt}-${index}`}>
                      <TableCell>{new Date(entry.createdAt).toFormattedDateTimeString()}</TableCell>
                      <TableCell>
                        <Chip size='small' variant='outlined' color={outcomeColour(entry.outcome)} label={entry.outcome} />
                      </TableCell>
                      <TableCell>{entry.failureReason ?? '-'}</TableCell>
                      <TableCell>{entry.packageIdentifier ?? '-'}</TableCell>
                      <TableCell><code>{entry.deviceHash ?? '-'}</code></TableCell>
                      <TableCell>{entry.deviceModel ?? '-'}</TableCell>
                      <TableCell>{entry.version ?? '-'}</TableCell>
                      <TableCell align='right'>{formatDuration(entry.heldSeconds)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mt: 2 }}>
                <Typography variant='caption' color='text.secondary'>
                  {logs.total} event(s)
                </Typography>
                <Pagination
                  size='small'
                  count={Math.max(1, Math.ceil(logs.total / logs.limit))}
                  page={logs.page}
                  onChange={(_event, value) => setPage(value)}
                />
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ActivationLogsPage;
